import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import SEO from '@/components/seo/SEO';
import { PROGRAMMATIC_VERTICALS, getProgrammaticVerticalBySlug } from '@/lib/programmaticData';

const VerticalFinancingPage: React.FC = () => {
  const { slug = '' } = useParams();
  const vertical = getProgrammaticVerticalBySlug(slug);

  if (!vertical) {
    return (
      <div className="min-h-screen bg-[#f8f5ef] px-6 pb-24 pt-28 text-slate-900 md:pt-32">
        <div className="container mx-auto max-w-4xl rounded-3xl border border-slate-200 bg-white p-8">
          <h1 className="font-sans text-3xl text-slate-900">Industry page not found</h1>
          <p className="mt-3 text-slate-600">Try one of the available financing playbooks.</p>
          <Button asChild className="mt-6 rounded-full bg-slate-900 text-white hover:bg-slate-800">
            <Link to="/financing">Browse solutions</Link>
          </Button>
        </div>
      </div>
    );
  }

  const appUrl = import.meta.env.VITE_APP_URL || '';
  const appSignUpUrl = appUrl ? `${appUrl}/auth?mode=signup` : '/auth?mode=signup';
  const monthly = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(vertical.monthlyExampleUsd);
  const avgTicket = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(vertical.avgTicketUsd);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'FAQPage',
        mainEntity: vertical.faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      },
      {
        '@type': 'FinancialProduct',
        name: `LendLifted ${vertical.industry} Financing Checkout`,
        description: vertical.bluf,
        category: 'Financing-enabled checkout software',
        provider: {
          '@type': 'Organization',
          name: 'LendLifted',
        },
        amount: {
          '@type': 'MonetaryAmount',
          currency: 'USD',
          value: vertical.avgTicketUsd,
        },
        termsOfService: `${import.meta.env.VITE_SITE_URL || 'https://lendlifted.com'}/terms-of-service`,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-[#f8f5ef] pb-24 text-slate-900">
      <SEO
        title={vertical.title}
        description={vertical.bluf}
        canonical={`/financing/${vertical.slug}`}
        jsonLd={jsonLd}
      />

      <section className="px-6 pb-12 pt-28 md:pt-32">
        <div className="container mx-auto max-w-6xl rounded-[2rem] border border-slate-800 bg-slate-950 p-8 text-white md:p-10">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-cyan-200">{vertical.industry}</p>
          <h1 className="mt-3 max-w-4xl font-sans text-3xl text-white md:text-5xl">{vertical.title}</h1>
          <p className="mt-5 max-w-4xl text-base text-slate-200 md:text-lg">{vertical.bluf}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button asChild className="rounded-full bg-white px-6 text-slate-900 hover:bg-slate-100">
              <a href={appSignUpUrl}>
                Start selling
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
            <Button asChild variant="outline" className="rounded-full border-slate-600 bg-transparent text-slate-100 hover:bg-slate-800">
              <Link to="/contact">Talk with implementation team</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="px-6 py-8">
        <div className="container mx-auto grid max-w-6xl gap-4 md:grid-cols-3">
          <Card className="rounded-3xl border border-slate-200 bg-white">
            <CardContent className="p-6">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Average Ticket</p>
              <p className="mt-2 font-sans text-3xl text-slate-900">{avgTicket}</p>
            </CardContent>
          </Card>
          <Card className="rounded-3xl border border-slate-200 bg-white">
            <CardContent className="p-6">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Sample Monthly Option</p>
              <p className="mt-2 font-sans text-3xl text-slate-900">{monthly}/mo</p>
            </CardContent>
          </Card>
          <Card className="rounded-3xl border border-slate-200 bg-white">
            <CardContent className="p-6">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Typical Sales Increase</p>
              <p className="mt-2 font-sans text-3xl text-slate-900">+{vertical.benchmarkLiftPercent}%</p>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="px-6 py-8">
        <div className="container mx-auto grid max-w-6xl gap-6 md:grid-cols-2">
          <Card className="rounded-3xl border border-slate-200 bg-white">
            <CardContent className="p-6">
              <h2 className="font-sans text-2xl text-slate-900">Buyer actions that often mean they are ready</h2>
              <ul className="mt-4 space-y-3">
                {vertical.buyerIntentSignals.map((signal) => (
                  <li key={signal} className="flex items-start gap-2 text-slate-700">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-cyan-600" />
                    <span>{signal}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <Card className="rounded-3xl border border-slate-200 bg-white">
            <CardContent className="p-6">
              <h2 className="font-sans text-2xl text-slate-900">Frequently asked questions</h2>
              <div className="mt-4 space-y-4">
                {vertical.faqs.map((faq) => (
                  <article key={faq.question}>
                    <h3 className="text-base font-semibold text-slate-900">{faq.question}</h3>
                    <p className="mt-1 text-sm text-slate-600">{faq.answer}</p>
                  </article>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="px-6 py-8">
        <div className="container mx-auto max-w-6xl rounded-3xl border border-slate-200 bg-white p-6">
          <h2 className="font-sans text-2xl text-slate-900">Related financing guides</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {PROGRAMMATIC_VERTICALS.filter((item) => item.slug !== vertical.slug)
              .slice(0, 5)
              .map((item) => (
                <Link
                  key={item.slug}
                  to={`/financing/${item.slug}`}
                  className="rounded-full border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:border-slate-900 hover:text-slate-900"
                >
                  {item.industry}
                </Link>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default VerticalFinancingPage;
